import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import QRCode from 'react-qr-code';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { QrCode, Plus, Download, Trash2, ExternalLink, Copy, Check, Palette, Table2 } from 'lucide-react';
import { toast } from 'sonner';

export default function QRMenuPage() {
  const { API, authHeaders, activeBranch } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const l = (ar, en) => isAr ? ar : en;

  const [qrList, setQrList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');

  const [formData, setFormData] = useState({
    table_number: '',
    theme_color: '#D97706',
    welcome_message_ar: 'مرحباً بكم',
    welcome_message_en: 'Welcome',
    show_prices: true,
    allow_ordering: true,
  });

  const fetchQRs = useCallback(async () => {
    if (!activeBranch?.id) return;
    try {
      const { data } = await axios.get(`${API}/qr-menu/list?branch_id=${activeBranch.id}`, authHeaders());
      setQrList(data.qr_menus || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [API, authHeaders, activeBranch]);

  useEffect(() => { fetchQRs(); }, [fetchQRs]);

  const handleGenerate = async () => {
    if (!activeBranch?.id) return;
    setGenerating(true);
    try {
      await axios.post(`${API}/qr-menu/generate?branch_id=${activeBranch.id}`, formData, authHeaders());
      toast.success(l('تم إنشاء رمز QR بنجاح', 'QR code generated'));
      setShowCreate(false);
      setFormData({ table_number: '', theme_color: '#D97706', welcome_message_ar: 'مرحباً بكم', welcome_message_en: 'Welcome', show_prices: true, allow_ordering: true });
      fetchQRs();
    } catch (e) {
      toast.error(e.response?.data?.detail || l('حدث خطأ', 'Error'));
    }
    setGenerating(false);
  };

  const handleDelete = async (qrId) => {
    try {
      await axios.delete(`${API}/qr-menu/${qrId}`, authHeaders());
      toast.success(l('تم الحذف', 'Deleted'));
      fetchQRs();
    } catch { toast.error(l('حدث خطأ', 'Error')); }
  };

  const menuUrl = (qr) => {
    const base = window.location.origin;
    return `${base}/menu/${activeBranch?.id}${qr.table_number ? `?table=${qr.table_number}` : ''}`;
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    toast.success(l('تم النسخ', 'Copied'));
    setTimeout(() => setCopied(''), 2000);
  };

  const downloadQR = (qrId) => {
    const svg = document.getElementById(`qr-svg-${qrId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const link = document.createElement('a');
      link.download = `qr-menu-${qrId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <ModuleLayout>
      <div className="space-y-6" data-testid="qr-menu-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <QrCode className="w-6 h-6 inline-block me-2 text-amber-500" />
              {l('قائمة QR', 'QR Menu')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {l('أنشئ رموز QR لقائمتك ليتمكن العملاء من المسح والطلب', 'Create QR codes for your menu so customers can scan and order')}
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-amber-500 hover:bg-amber-600 text-white gap-2" data-testid="create-qr-btn">
            <Plus className="w-4 h-4" /> {l('إنشاء رمز QR', 'Create QR Code')}
          </Button>
        </div>

        {/* QR Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : qrList.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <QrCode className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">{l('لا توجد رموز QR بعد', 'No QR codes yet')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{l('أنشئ أول رمز QR لقائمتك', 'Create your first QR menu code')}</p>
              <Button onClick={() => setShowCreate(true)} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" /> {l('إنشاء الآن', 'Create Now')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {qrList.map(qr => {
              const url = menuUrl(qr);
              return (
                <Card key={qr.id} className="overflow-hidden" data-testid={`qr-card-${qr.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          {qr.table_number ? (
                            <span className="text-sm font-bold text-foreground flex items-center gap-1.5">
                              <Table2 className="w-4 h-4 text-amber-500" />
                              {l('طاولة', 'Table')} #{qr.table_number}
                            </span>
                          ) : (
                            <span className="text-sm font-bold text-foreground">{l('القائمة العامة', 'General Menu')}</span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {new Date(qr.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US')}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => copyUrl(url, qr.id)} data-testid={`copy-qr-${qr.id}`}>
                          {copied === qr.id ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadQR(qr.id)} data-testid={`download-qr-${qr.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => handleDelete(qr.id)} data-testid={`delete-qr-${qr.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 flex justify-center" style={{ border: `2px solid ${qr.theme_color || '#D97706'}` }}>
                      <QRCode id={`qr-svg-${qr.id}`} value={url} size={160} fgColor={qr.theme_color || '#000000'} />
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-1 text-xs text-primary hover:underline justify-center" data-testid={`preview-qr-${qr.id}`}>
                      <ExternalLink className="w-3 h-3" /> {l('معاينة القائمة', 'Preview Menu')}
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="sm:max-w-md" data-testid="create-qr-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cairo, sans-serif' }}>{l('إنشاء رمز QR جديد', 'Create New QR Code')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{l('رقم الطاولة (اختياري)', 'Table Number (optional)')}</label>
                <Input value={formData.table_number} onChange={e => setFormData({ ...formData, table_number: e.target.value })}
                  placeholder={l('مثل: 1، 2، VIP1', 'e.g. 1, 2, VIP1')} data-testid="qr-table-input" />
                <p className="text-[11px] text-muted-foreground mt-1">{l('اتركه فارغاً لقائمة عامة', 'Leave empty for general menu')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{l('لون القائمة', 'Theme Color')}</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={formData.theme_color} onChange={e => setFormData({ ...formData, theme_color: e.target.value })}
                    className="w-10 h-10 rounded-lg border cursor-pointer" data-testid="qr-color-input" />
                  <div className="flex gap-2">
                    {['#D97706', '#2563EB', '#10B981', '#EF4444', '#7C3AED', '#000000'].map(c => (
                      <button key={c} onClick={() => setFormData({ ...formData, theme_color: c })}
                        className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                        style={{ background: c, borderColor: formData.theme_color === c ? c : 'transparent' }} />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">{l('رسالة الترحيب', 'Welcome Message')}</label>
                <Input value={isAr ? formData.welcome_message_ar : formData.welcome_message_en}
                  onChange={e => setFormData({ ...formData, [isAr ? 'welcome_message_ar' : 'welcome_message_en']: e.target.value })}
                  data-testid="qr-welcome-input" />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.show_prices}
                    onChange={e => setFormData({ ...formData, show_prices: e.target.checked })}
                    className="rounded border-border" data-testid="qr-prices-toggle" />
                  <span className="text-sm">{l('إظهار الأسعار', 'Show Prices')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.allow_ordering}
                    onChange={e => setFormData({ ...formData, allow_ordering: e.target.checked })}
                    className="rounded border-border" data-testid="qr-ordering-toggle" />
                  <span className="text-sm">{l('السماح بالطلب', 'Allow Ordering')}</span>
                </label>
              </div>
              {/* Preview */}
              <div className="bg-muted/50 rounded-lg p-4 flex justify-center">
                <QRCode value={`${window.location.origin}/menu/${activeBranch?.id || 'preview'}${formData.table_number ? `?table=${formData.table_number}` : ''}`}
                  size={140} fgColor={formData.theme_color} />
              </div>
              <Button onClick={handleGenerate} disabled={generating} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="generate-qr-btn">
                {generating ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : l('إنشاء رمز QR', 'Generate QR Code')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
